import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiFileText, FiSearch, FiDownload, FiPrinter, FiDollarSign,
  FiHash, FiPercent, FiExternalLink, FiChevronUp, FiChevronDown, FiChevronRight
} from 'react-icons/fi';
import { colors } from '../../theme/colors';
import { collection, getDocs, query, orderBy, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { getEmployeesCached, getJobsCached } from '../../utils/masterDataStore';
import { recordReads } from '../../utils/readTelemetry';
import { useAuth } from '../../context/AuthContext';
import PageLayout from '../../components/PageLayout';
import CustomerSelect from '../../components/CustomerSelect';
import { formatNumber, formatDate } from '../../utils/formatters';
import useCurrency from '../../hooks/useCurrency';
import useReportMemory from '../../hooks/useReportMemory';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, LineChart, Line
} from 'recharts';
import { addPrintBrand } from '../../utils/printBranding';
import { getTaxTerms } from '../../utils/taxTerms';

// Inline styles for the expandable per-invoice line-item sub-table.
const DETAIL_TH = { padding: '4px 8px', fontSize: '11px', fontWeight: 600, borderBottom: '1px solid #e5e7eb', textAlign: 'left' };
const DETAIL_TD = { padding: '4px 8px', borderBottom: '1px solid #f1f5f9' };

// Sale document type codes and labels. Sale Register shows SALES documents only
// (no Delivery Challan / Quotation / Sale Order — those are non-financial).
const SALE_TYPE_CODES = ['SI', 'NSI', 'PS', 'SR', 'NSR'];

const SALE_TYPE_LABELS = {
  SI: 'Sale Invoice',
  NSI: 'Non-Tax Sale Invoice',
  PS: 'POS Sale',
  SR: 'Sale Return',
  NSR: 'Non-Tax Sale Return',
};

// Document-type checkbox options (replaces the old Document Type + Tax dropdowns).
const DOC_TYPE_OPTIONS = [
  { code: 'SI', label: 'Tax Sale' },
  { code: 'NSI', label: 'Non-Tax Sale' },
  { code: 'PS', label: 'POS Sale' },
  { code: 'SR', label: 'Tax Return' },
  { code: 'NSR', label: 'Non-Tax Return' },
];

// Route map for navigating to document pages
const SALE_TYPE_ROUTES = {
  SI: '/sales/sale-invoice',
  NSI: '/sales/non-tax-invoice',
  PS: '/pos',
  SR: '/sales/sale-return',
  NSR: '/sales/non-tax-sale-return',
  DC: '/sales/delivery-challan',
  QT: '/sales/quotation',
  SO: '/sales/sale-order'
};

// Status options — "Posted" covers all posted invoices (Posted + Paid + Partial).
const STATUS_OPTIONS = ['All', 'Draft', 'Posted', 'Cancelled'];

// Status color map
const getStatusColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'posted' || s === 'approved') return { bg: '#E8F5E9', color: '#2E7D32' };
  if (s === 'paid') return { bg: '#E8F5E9', color: '#1B5E20' };
  if (s === 'partial') return { bg: '#FFF3E0', color: '#E65100' };
  if (s === 'draft' || s === 'pending') return { bg: '#FFF8E1', color: '#F57F17' };
  if (s === 'cancelled') return { bg: '#FFEBEE', color: '#C62828' };
  if (s === 'closed') return { bg: '#E3F2FD', color: '#1565C0' };
  return { bg: colors.neutral[100], color: colors.text.secondary };
};

// Chart colors
const CHART_COLORS = ['#800020', '#A0153E', '#5D0017', '#C4A484', '#8B7355', '#D4AF37'];

const SaleRegister = () => {
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const location = useLocation();
  const { activeCompany, activeFinancialYear, activeBranch } = useAuth();
  const taxLabel = activeCompany?.taxLabel || 'GST';
  // Customer NTN column (2026-10-28): from the customer master ("CR" in VAT mode).
  const ntnLabel = getTaxTerms(activeCompany).registrationLabel;
  // Backward memory: restore filters + generated rows on Back (0 re-reads).
  const [savedMem, saveMemory] = useReportMemory('sale-register');

  // Data state
  const [transactions, setTransactions] = useState(() => savedMem?.transactions ?? []);
  const [expandedRows, setExpandedRows] = useState(() => savedMem?.expandedRows ?? new Set());
  const toggleRow = (id) => setExpandedRows(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [financialYears, setFinancialYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(() => savedMem?.hasLoaded ?? false);

  // Filter state
  const [selectedFYId, setSelectedFYId] = useState(() => savedMem?.selectedFYId ?? '');
  const [dateFrom, setDateFrom] = useState(() => savedMem?.dateFrom ?? '');
  const [dateTo, setDateTo] = useState(() => savedMem?.dateTo ?? '');
  const [docTypes, setDocTypes] = useState(() => savedMem?.docTypes ?? { SI: true, NSI: true, PS: true, SR: true, NSR: true });
  const toggleDocType = (code) => setDocTypes(prev => ({ ...prev, [code]: !prev[code] }));
  const [customerFilter, setCustomerFilter] = useState(() => savedMem?.customerFilter ?? 'All');
  const [salespersonFilter, setSalespersonFilter] = useState(() => savedMem?.salespersonFilter ?? 'All');
  const [jobFilter, setJobFilter] = useState(() => savedMem?.jobFilter ?? 'All');
  // FLW-05: default to posted documents (Posted / Partial / Paid) — Draft and
  // Cancelled are left out unless picked in the Status filter.
  const [statusFilter, setStatusFilter] = useState(() => savedMem?.statusFilter ?? 'Posted');
  const [itemFilter, setItemFilter] = useState(() => savedMem?.itemFilter ?? 'All');   // filter invoices containing this item
  const [items, setItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState(() => savedMem?.searchQuery ?? '');
  const [regions, setRegions] = useState([]);
  const [areas, setAreas] = useState([]);
  const [selectedRegionId, setSelectedRegionId] = useState(() => savedMem?.selectedRegionId ?? 'all');
  const [selectedAreaId, setSelectedAreaId] = useState(() => savedMem?.selectedAreaId ?? 'all');

  // Sort state
  const [sortField, setSortField] = useState(() => savedMem?.sortField ?? 'docDate');
  const [sortDirection, setSortDirection] = useState(() => savedMem?.sortDirection ?? 'desc');

  // Load financial years
  const loadFinancialYears = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      const fyRef = collection(db, 'companies', activeCompany.id, 'financialYears');
      const q = query(fyRef, orderBy('startDate', 'desc'));
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);
      const fys = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setFinancialYears(fys);

      if (activeFinancialYear?.id) {
        setSelectedFYId(activeFinancialYear.id);
      } else if (fys.length > 0) {
        setSelectedFYId(fys[0].id);
      }
    } catch (error) {
      console.error('Error loading financial years:', error);
    }
  }, [activeCompany, activeFinancialYear]);

  // Load dropdown data (customers, employees, jobs)
  const loadDropdownData = useCallback(async () => {
    if (!activeCompany?.id) return;
    try {
      // Load customers
      const custRef = collection(db, 'companies', activeCompany.id, 'customers');
      const custSnapshot = await getDocs(query(custRef, orderBy('companyName')));
      setCustomers(custSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load employees (salespersons)
      const empRows = await getEmployeesCached(activeCompany.id);
      setEmployees(empRows.filter(r => r.firstName != null)
        .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || ''))));

      // Load jobs
      const jobRows = await getJobsCached(activeCompany.id);
      setJobs(jobRows.filter(r => r.jobName != null)
        .sort((a, b) => String(a.jobName || '').localeCompare(String(b.jobName || ''))));

      // Load regions
      const regRef = collection(db, 'companies', activeCompany.id, 'regions');
      const regSnapshot = await getDocs(regRef);
      setRegions(regSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load areas
      const areaRef = collection(db, 'companies', activeCompany.id, 'areas');
      const areaSnapshot = await getDocs(areaRef);
      setAreas(areaSnapshot.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load items (for the item filter)
      const itemRef = collection(db, 'companies', activeCompany.id, 'inventoryItems');
      const itemSnapshot = await getDocs(query(itemRef, orderBy('itemCode')));
      setItems(itemSnapshot.docs.map(d => ({ id: d.id, ...d.data() })).filter(i => i.isDeleted !== true));

      recordReads(activeCompany.id, custSnapshot.size + regSnapshot.size + areaSnapshot.size + itemSnapshot.size);
    } catch (error) {
      console.error('Error loading dropdown data:', error);
    }
  }, [activeCompany]);

  // Initialize
  useEffect(() => {
    loadFinancialYears();
    loadDropdownData();

    // Set default date range to current month — ONLY on a fresh visit.
    // If we restored a saved view (Back navigation), keep the restored dates.
    if (!savedMem) {
      const today = new Date();
      const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
      setDateFrom(firstDay.toISOString().split('T')[0]);
      setDateTo(today.toISOString().split('T')[0]);
    }
  }, [loadFinancialYears, loadDropdownData, savedMem]);

  // Handle incoming navigation state for pre-applied filters
  const processedStateRef = useRef(false);
  useEffect(() => {
    if (processedStateRef.current) return;
    const state = location.state;
    if (!state) return;

    if (state.filterCustomerId && customers.length > 0) {
      const customer = customers.find(c => c.id === state.filterCustomerId);
      if (customer) {
        processedStateRef.current = true;
        setCustomerFilter(state.filterCustomerId);
        setDateFrom('');
        setDateTo('');
      }
    } else if (state.filterSalesperson && employees.length > 0) {
      const employee = employees.find(e => e.id === state.filterSalesperson);
      if (employee) {
        processedStateRef.current = true;
        setSalespersonFilter(state.filterSalesperson);
        setDateFrom('');
        setDateTo('');
      }
    }
  }, [location.state, customers, employees]);

  // Customer map for region/area filtering
  const customerMap = useMemo(() => {
    const map = {};
    customers.forEach(c => { map[c.id] = c; });
    return map;
  }, [customers]);
  const ntnOf = (t) => String(customerMap[t.partyId]?.ntn || t.partyNTN || '').trim();

  // Cascading areas based on selected region
  const filteredAreas = useMemo(() => {
    if (selectedRegionId === 'all') return areas;
    return areas.filter(a => a.regionId === selectedRegionId);
  }, [areas, selectedRegionId]);

  // Load transactions
  const loadTransactions = useCallback(async () => {
    if (!activeCompany?.id) return;

    setLoading(true);
    setHasLoaded(true);

    try {
      const transRef = collection(db, 'companies', activeCompany.id, 'transactions');

      // Build query conditions
      const conditions = [];

      // Filter by financial year
      if (selectedFYId) {
        conditions.push(where('financialYearId', '==', selectedFYId));
      }

      // Fetch all sale documents; the doc-type checkboxes filter them in memory.
      conditions.push(where('tranTypeCode', 'in', SALE_TYPE_CODES));

      const q = query(transRef, ...conditions);
      const snapshot = await getDocs(q);
      recordReads(activeCompany.id, snapshot.size);

      let allTransactions = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })).filter(t => {
        if (!activeBranch || activeBranch.id === 'all') return true;
        return t.branchId === activeBranch.id;
      });

      // Document-type checkboxes (Tax Sale / Non-Tax Sale / POS / Tax Return / Non-Tax Return)
      allTransactions = allTransactions.filter(t => docTypes[t.tranTypeCode]);

      // Apply date filters in memory
      if (dateFrom) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') >= dateFrom);
      }
      if (dateTo) {
        allTransactions = allTransactions.filter(t => (t.docDate || '') <= dateTo);
      }

      // Apply customer filter
      if (customerFilter !== 'All') {
        allTransactions = allTransactions.filter(t => t.partyId === customerFilter);
      }

      // Apply region filter
      if (selectedRegionId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.regionId === selectedRegionId;
        });
      }

      // Apply area filter
      if (selectedAreaId !== 'all') {
        allTransactions = allTransactions.filter(t => {
          const cust = customerMap[t.partyId];
          return cust && cust.areaId === selectedAreaId;
        });
      }

      // Apply salesperson filter
      if (salespersonFilter !== 'All') {
        allTransactions = allTransactions.filter(t => t.employeeId === salespersonFilter);
      }

      // Apply job filter
      if (jobFilter !== 'All') {
        allTransactions = allTransactions.filter(t => t.jobId === jobFilter);
      }

      // Apply status filter — Posted covers Posted + Paid + Partial.
      if (statusFilter !== 'All') {
        const wanted = statusFilter === 'Posted' ? ['posted', 'paid', 'partial'] : [statusFilter.toLowerCase()];
        allTransactions = allTransactions.filter(t => wanted.includes((t.status || '').toLowerCase()));
      }

      // Item filter — keep only invoices that contain the selected item.
      if (itemFilter !== 'All') {
        allTransactions = allTransactions.filter(t =>
          Array.isArray(t.lines) && t.lines.some(l => l.itemId === itemFilter)
        );
      }

      // Apply search query on doc number
      if (searchQuery.trim()) {
        const sq = searchQuery.toLowerCase().trim();
        allTransactions = allTransactions.filter(t =>
          (t.docNumber || '').toLowerCase().includes(sq)
        );
      }

      // Sort by docDate descending by default
      allTransactions.sort((a, b) => {
        const dateCompare = (b.docDate || '').localeCompare(a.docDate || '');
        if (dateCompare !== 0) return dateCompare;
        return (b.docNumber || '').localeCompare(a.docNumber || '');
      });

      setTransactions(allTransactions);
      // Open every invoice's line items by default (no manual expanding).
      const expanded = new Set(allTransactions.filter(t => Array.isArray(t.lines) && t.lines.length > 0).map(t => t.id));
      setExpandedRows(expanded);

      // Remember this generated view so Back restores it with 0 re-reads.
      saveMemory({
        transactions: allTransactions,
        expandedRows: expanded,
        hasLoaded: true,
        selectedFYId, dateFrom, dateTo, docTypes,
        customerFilter, salespersonFilter, jobFilter, statusFilter, itemFilter,
        searchQuery, selectedRegionId, selectedAreaId, sortField, sortDirection
      });
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [activeCompany, selectedFYId, docTypes, dateFrom, dateTo,
      customerFilter, salespersonFilter, jobFilter, statusFilter, itemFilter, searchQuery, activeBranch,
      customerMap, selectedRegionId, selectedAreaId, saveMemory, sortField, sortDirection]);

  // Sort handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sorted transactions
  const sortedTransactions = useMemo(() => {
    const sorted = [...transactions];
    sorted.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle numeric fields
      if (['totalAmount', 'netAmount', 'totalGST', 'totalWHT', 'totalDiscount'].includes(sortField)) {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [transactions, sortField, sortDirection]);

  // Summary calculations — returns subtract from totals
  const summary = useMemo(() => {
    const sign = (t) => (t.tranTypeCode === 'SR' || t.tranTypeCode === 'NSR') ? -1 : 1;
    return {
      totalDocuments: transactions.length,
      totalGrossAmount: transactions.reduce((sum, t) => sum + sign(t) * (Number(t.totalAmount) || 0), 0),
      totalNetAmount: transactions.reduce((sum, t) => sum + sign(t) * (Number(t.netAmount) || 0), 0),
      totalGST: transactions.reduce((sum, t) => sum + sign(t) * (Number(t.totalGST) || 0), 0),
      totalDiscount: transactions.reduce((sum, t) => sum + sign(t) * (Number(t.totalDiscount) || 0), 0),
      totalWHT: transactions.reduce((sum, t) => sum + sign(t) * (Number(t.totalWHT) || 0), 0)
    };
  }, [transactions]);

  // Chart data: Daily/Monthly document count trend
  const trendChartData = useMemo(() => {
    if (transactions.length === 0) return [];

    // Determine if we should group by day or month
    const dateSpread = new Set(transactions.map(t => t.docDate)).size;
    const useMonthly = dateSpread > 60;

    const grouped = {};
    transactions.forEach(t => {
      const date = t.docDate || '';
      let key;
      if (useMonthly && date.length >= 7) {
        key = date.substring(0, 7); // YYYY-MM
      } else {
        key = date;
      }
      if (!grouped[key]) {
        grouped[key] = { period: key, count: 0, amount: 0 };
      }
      grouped[key].count += 1;
      grouped[key].amount += Number(t.netAmount) || 0;
    });

    return Object.values(grouped).sort((a, b) => a.period.localeCompare(b.period)).map(item => ({
      ...item,
      label: useMonthly
        ? formatMonthLabel(item.period)
        : formatShortDate(item.period)
    }));
  }, [transactions]);

  // Chart data: Amount by document type
  const typeChartData = useMemo(() => {
    if (transactions.length === 0) return [];

    const grouped = {};
    transactions.forEach(t => {
      const type = t.tranTypeCode || 'Other';
      if (!grouped[type]) {
        grouped[type] = { type, label: SALE_TYPE_LABELS[type] || type, amount: 0, count: 0 };
      }
      grouped[type].amount += Number(t.netAmount) || 0;
      grouped[type].count += 1;
    });

    return Object.values(grouped).sort((a, b) => b.amount - a.amount);
  }, [transactions]);

  // Navigate to document
  const handleDocClick = (tranTypeCode, transactionId) => {
    const route = SALE_TYPE_ROUTES[tranTypeCode];
    if (route) {
      navigate(route, { state: { viewTransactionId: transactionId } });
    }
  };

  // CSV Export
  const handleExport = () => {
    if (sortedTransactions.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    let csv = `Sale Register Report\n`;
    csv += `Company: ${activeCompany?.companyName || ''}\n`;
    csv += `Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''}\n`;
    csv += `Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'}\n\n`;
    csv += `#,Date,Doc Number,Reference,Type,Customer,Customer ${ntnLabel},Salesperson,Job,Gross Amount,Discount,${taxLabel},WHT,Net Amount,Status,Remarks\n`;

    sortedTransactions.forEach((t, idx) => {
      csv += `${idx + 1},`;
      csv += `${formatDate(t.docDate)},`;
      csv += `${t.docNumber || ''},`;
      csv += `"${(t.customerPO || t.reference || '').replace(/"/g, '""')}",`;
      csv += `${SALE_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || ''},`;
      csv += `"${(t.partyName || '').replace(/"/g, '""')}",`;
      csv += `"${ntnOf(t).replace(/"/g, '""')}",`;
      csv += `"${(t.employeeName || '').replace(/"/g, '""')}",`;
      csv += `"${(t.jobName || '').replace(/"/g, '""')}",`;
      csv += `${Number(t.totalAmount) || 0},`;
      csv += `${Number(t.totalDiscount) || 0},`;
      csv += `${Number(t.totalGST) || 0},`;
      csv += `${Number(t.totalWHT) || 0},`;
      csv += `${Number(t.netAmount) || 0},`;
      csv += `${t.status || ''},`;
      csv += `"${(t.notes || '').replace(/"/g, '""')}"\n`;
      // Line items under each invoice
      (Array.isArray(t.lines) ? t.lines : []).forEach(l => {
        const itemLabel = ((l.itemCode ? l.itemCode + ' - ' : '') + (l.itemName || l.description || '')).replace(/"/g, '""');
        csv += `,,,,  Item,"${itemLabel}",,"Qty ${Number(l.quantity) || 0} ${l.unitCode || l.unitOfMeasure || ''}",${Number(l.unitPrice) || 0},${Number(l.discountAmount) || 0},${Number(l.taxAmount) || 0},${Number(l.lineTotal) || 0},,\n`;
      });
    });

    csv += `\n,,,,,,,,Total,${summary.totalGrossAmount},${summary.totalDiscount},${summary.totalGST},${summary.totalWHT},${summary.totalNetAmount},,\n`;
    csv += `\nTotal Documents: ${summary.totalDocuments}\n`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Sale_Register_${dateFrom || 'all'}_to_${dateTo || 'all'}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Print
  const handlePrint = () => {
    if (sortedTransactions.length === 0) return;

    const selectedFY = financialYears.find(f => f.id === selectedFYId);

    const w = window.open('', '_blank');
    w.document.write(`<html><head><title>Sale Register</title>
      <style>
        body{font-family:'Segoe UI',sans-serif;margin:30px;color:#1A1A1A;font-size:12px}
        h1{color:#800020;font-size:20px;margin-bottom:5px}
        .meta{color:#666;font-size:12px;margin-bottom:15px}
        .summary{display:flex;gap:24px;margin-bottom:20px;padding:12px;background:#f9f9f9;border-radius:8px}
        .summary-item{text-align:center}
        .summary-label{font-size:10px;color:#666;margin-bottom:3px}
        .summary-value{font-size:14px;font-weight:600}
        table{width:100%;border-collapse:collapse;margin-bottom:15px}
        th{background:#800020;color:white;padding:6px 8px;text-align:left;font-size:10px;text-transform:uppercase}
        td{padding:6px 8px;border-bottom:1px solid #ddd;font-size:11px}
        .amount{text-align:right;font-family:'Consolas',monospace}
        .totals-row{background:#f5f5f5;font-weight:600}
        .status-badge{padding:2px 6px;border-radius:3px;font-size:10px;font-weight:500}
        @media print{body{margin:15px}h1{font-size:16px}}
      </style></head><body>
      <h1>Sale Register</h1>
      <div class="meta">
        ${activeCompany?.companyName || 'Company'} |
        Financial Year: ${selectedFY?.yearName || selectedFY?.yearCode || ''} |
        Period: ${dateFrom ? formatDate(dateFrom) : 'Start'} to ${dateTo ? formatDate(dateTo) : 'End'} |
        Documents: ${summary.totalDocuments}
      </div>
      <div class="summary">
        <div class="summary-item">
          <div class="summary-label">Total Documents</div>
          <div class="summary-value">${formatNumber(summary.totalDocuments)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Gross</div>
          <div class="summary-value">${formatCurrency(summary.totalGrossAmount)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total Net</div>
          <div class="summary-value">${formatCurrency(summary.totalNetAmount)}</div>
        </div>
        <div class="summary-item">
          <div class="summary-label">Total ${taxLabel}</div>
          <div class="summary-value">${formatCurrency(summary.totalGST)}</div>
        </div>
      </div>
      <table>
        <thead><tr>
          <th>#</th>
          <th>Date</th>
          <th>Doc Number</th>
          <th>Reference</th>
          <th>Type</th>
          <th>Customer</th>
          <th>${ntnLabel}</th>
          <th>Salesperson</th>
          <th class="amount">Gross</th>
          <th class="amount">Discount</th>
          <th class="amount">${taxLabel}</th>
          <th class="amount">WHT</th>
          <th class="amount">Net Amount</th>
          <th>Status</th>
        </tr></thead>
        <tbody>
          ${sortedTransactions.map((t, idx) => {
            const lines = Array.isArray(t.lines) ? t.lines : [];
            const itemRows = lines.length ? `
              <tr class="item-block"><td></td><td colspan="13" style="padding:4px 8px 8px 24px;background:#fafafa">
                <table style="width:100%;border-collapse:collapse">
                  <thead><tr>
                    <th style="text-align:left;font-size:9px;color:#666;padding:2px 6px">Item</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">Qty</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">Rate</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">Disc</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">${taxLabel}</th>
                    <th style="text-align:right;font-size:9px;color:#666;padding:2px 6px">Amount</th>
                  </tr></thead>
                  <tbody>${lines.map(l => `<tr>
                    <td style="padding:2px 6px;font-size:10px">${(l.itemCode ? l.itemCode + ' - ' : '') + (l.itemName || l.description || '-')}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${Number(l.quantity) || 0} ${l.unitCode || l.unitOfMeasure || ''}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${formatCurrency(Number(l.unitPrice) || 0)}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${formatCurrency(Number(l.discountAmount) || 0)}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${formatCurrency(Number(l.taxAmount) || 0)}</td>
                    <td style="padding:2px 6px;font-size:10px;text-align:right">${formatCurrency(Number(l.lineTotal) || 0)}</td>
                  </tr>`).join('')}</tbody>
                </table>
              </td></tr>` : '';
            return `
            <tr>
              <td>${idx + 1}</td>
              <td>${formatDate(t.docDate)}</td>
              <td>${t.docNumber || '-'}</td>
              <td>${t.customerPO || t.reference || '-'}</td>
              <td>${SALE_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || '-'}</td>
              <td>${t.partyName || '-'}</td>
              <td>${ntnOf(t) || '-'}</td>
              <td>${t.employeeName || '-'}</td>
              <td class="amount">${formatCurrency(Number(t.totalAmount) || 0)}</td>
              <td class="amount">${formatCurrency(Number(t.totalDiscount) || 0)}</td>
              <td class="amount">${formatCurrency(Number(t.totalGST) || 0)}</td>
              <td class="amount">${formatCurrency(Number(t.totalWHT) || 0)}</td>
              <td class="amount">${formatCurrency(Number(t.netAmount) || 0)}</td>
              <td>${t.status || '-'}</td>
            </tr>${itemRows}`;
          }).join('')}
          <tr class="totals-row">
            <td colspan="8" style="text-align:right">Totals</td>
            <td class="amount">${formatCurrency(summary.totalGrossAmount)}</td>
            <td class="amount">${formatCurrency(summary.totalDiscount)}</td>
            <td class="amount">${formatCurrency(summary.totalGST)}</td>
            <td class="amount">${formatCurrency(summary.totalWHT)}</td>
            <td class="amount">${formatCurrency(summary.totalNetAmount)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
    </body></html>`);
    addPrintBrand(w);
    w.document.close();
    w.print();
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <TooltipBox>
          <TooltipLabel>{label}</TooltipLabel>
          {payload.map((entry, index) => (
            <TooltipRow key={index}>
              <TooltipDot style={{ background: entry.color }} />
              <span>{entry.name}: </span>
              <strong>
                {entry.name === 'Count' || entry.name === 'Documents'
                  ? formatNumber(entry.value)
                  : formatCurrency(entry.value)}
              </strong>
            </TooltipRow>
          ))}
        </TooltipBox>
      );
    }
    return null;
  };

  return (
    <PageLayout pageTitle="Sale Register" breadcrumb="Reports / Sale Reports / Sale Register">
      {/* Filters */}
      <FiltersCard>
        <FiltersGrid>
          <FilterGroup>
            <Label>Financial Year</Label>
            <Select value={selectedFYId} onChange={(e) => setSelectedFYId(e.target.value)}>
              <option value="">All Financial Years</option>
              {financialYears.map(fy => (
                <option key={fy.id} value={fy.id}>
                  {fy.yearName || fy.yearCode}
                  {fy.isActive ? ' (Active)' : ''}
                </option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Date From</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Date To</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </FilterGroup>

          <FilterGroup style={{ gridColumn: 'span 2' }}>
            <Label>Document Types</Label>
            <DocTypeChecks>
              {DOC_TYPE_OPTIONS.map(o => (
                <DocCheck key={o.code}>
                  <input type="checkbox" checked={!!docTypes[o.code]} onChange={() => toggleDocType(o.code)} />
                  <span>{o.label}</span>
                </DocCheck>
              ))}
            </DocTypeChecks>
          </FilterGroup>

          <FilterGroup>
            <Label>Item</Label>
            <Select value={itemFilter} onChange={(e) => setItemFilter(e.target.value)}>
              <option value="All">All Items</option>
              {items.map(it => (
                <option key={it.id} value={it.id}>{it.itemCode ? it.itemCode + ' - ' : ''}{it.itemName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Customer</Label>
            <CustomerSelect
              customers={customers}
              selectedCustomerId={customerFilter}
              onChange={setCustomerFilter}
              allOption={{ value: 'All', label: 'All Customers' }}
            />
          </FilterGroup>

          <FilterGroup>
            <Label>Region</Label>
            <Select value={selectedRegionId} onChange={e => { setSelectedRegionId(e.target.value); setSelectedAreaId('all'); }}>
              <option value="all">All Regions</option>
              {regions.map(r => (
                <option key={r.id} value={r.id}>{r.regionName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Area</Label>
            <Select value={selectedAreaId} onChange={e => setSelectedAreaId(e.target.value)}>
              <option value="all">All Areas</option>
              {filteredAreas.map(a => (
                <option key={a.id} value={a.id}>{a.areaName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Salesperson</Label>
            <Select value={salespersonFilter} onChange={(e) => setSalespersonFilter(e.target.value)}>
              <option value="All">All Salespersons</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.fullName || e.firstName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Job</Label>
            <Select value={jobFilter} onChange={(e) => setJobFilter(e.target.value)}>
              <option value="All">All Jobs</option>
              {jobs.map(j => (
                <option key={j.id} value={j.id}>{j.jobName}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Status</Label>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              {STATUS_OPTIONS.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </Select>
          </FilterGroup>

          <FilterGroup>
            <Label>Search Doc #</Label>
            <Input
              type="text"
              placeholder="Search document number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </FilterGroup>
        </FiltersGrid>

        <ButtonGroup>
          <Button onClick={handleExport} disabled={sortedTransactions.length === 0}>
            <FiDownload /> Export CSV
          </Button>
          <Button onClick={handlePrint} disabled={sortedTransactions.length === 0}>
            <FiPrinter /> Print
          </Button>
          <Button primary onClick={loadTransactions} disabled={loading}>
            <FiSearch /> {loading ? 'Loading...' : 'Generate Report'}
          </Button>
        </ButtonGroup>
      </FiltersCard>

      {/* Summary Cards */}
      {hasLoaded && (
        <SummaryCards>
          <SummaryCardStyled>
            <SummaryIcon color={colors.primary.main}>
              <FiHash />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Documents</SummaryLabel>
              <SummaryValue>{formatNumber(summary.totalDocuments)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.chart.secondary}>
              <FiDollarSign />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Gross Amount</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalGrossAmount)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.success.main}>
              <FiDollarSign />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total Net Amount</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalNetAmount)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>

          <SummaryCardStyled>
            <SummaryIcon color={colors.info.main}>
              <FiPercent />
            </SummaryIcon>
            <SummaryInfo>
              <SummaryLabel>Total {taxLabel}</SummaryLabel>
              <SummaryValue>{formatCurrency(summary.totalGST)}</SummaryValue>
            </SummaryInfo>
          </SummaryCardStyled>
        </SummaryCards>
      )}

      {/* Data Table */}
      <ContentCard>
        <TableContainer>
          {loading ? (
            <EmptyState>
              <EmptyText>Loading sale transactions...</EmptyText>
            </EmptyState>
          ) : !hasLoaded ? (
            <EmptyState>
              <EmptyIcon><FiFileText /></EmptyIcon>
              <EmptyText>Select filters and click "Generate Report" to view the sale register</EmptyText>
            </EmptyState>
          ) : sortedTransactions.length === 0 ? (
            <EmptyState>
              <EmptyIcon><FiFileText /></EmptyIcon>
              <EmptyText>No sale transactions found for the selected criteria</EmptyText>
            </EmptyState>
          ) : (
            <Table>
              <Thead>
                <Tr>
                  <Th>#</Th>
                  <ThSortable
                    onClick={() => handleSort('docDate')}
                    $active={sortField === 'docDate'}
                  >
                    Date
                    <SortIcon>
                      {sortField === 'docDate'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    onClick={() => handleSort('docNumber')}
                    $active={sortField === 'docNumber'}
                  >
                    Doc Number
                    <SortIcon>
                      {sortField === 'docNumber'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <Th>Reference</Th>
                  <ThSortable
                    onClick={() => handleSort('tranTypeCode')}
                    $active={sortField === 'tranTypeCode'}
                  >
                    Type
                    <SortIcon>
                      {sortField === 'tranTypeCode'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    onClick={() => handleSort('partyName')}
                    $active={sortField === 'partyName'}
                  >
                    Customer
                    <SortIcon>
                      {sortField === 'partyName'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <Th>{ntnLabel}</Th>
                  <ThSortable
                    onClick={() => handleSort('employeeName')}
                    $active={sortField === 'employeeName'}
                  >
                    Salesperson
                    <SortIcon>
                      {sortField === 'employeeName'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    align="right"
                    onClick={() => handleSort('totalAmount')}
                    $active={sortField === 'totalAmount'}
                  >
                    Gross Amount
                    <SortIcon>
                      {sortField === 'totalAmount'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <Th align="right">Discount</Th>
                  <Th align="right">{taxLabel}</Th>
                  <Th align="right">WHT</Th>
                  <ThSortable
                    align="right"
                    onClick={() => handleSort('netAmount')}
                    $active={sortField === 'netAmount'}
                  >
                    Net Amount
                    <SortIcon>
                      {sortField === 'netAmount'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                  <ThSortable
                    onClick={() => handleSort('status')}
                    $active={sortField === 'status'}
                  >
                    Status
                    <SortIcon>
                      {sortField === 'status'
                        ? (sortDirection === 'asc' ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />)
                        : <FiChevronDown size={12} style={{ opacity: 0.3 }} />
                      }
                    </SortIcon>
                  </ThSortable>
                </Tr>
              </Thead>
              <Tbody>
                {sortedTransactions.map((t, idx) => {
                  const isOpen = expandedRows.has(t.id);
                  const lines = Array.isArray(t.lines) ? t.lines : [];
                  return (
                  <React.Fragment key={t.id}>
                  <Tr>
                    <Td
                      onClick={() => lines.length && toggleRow(t.id)}
                      style={{ cursor: lines.length ? 'pointer' : 'default', whiteSpace: 'nowrap' }}
                      title={lines.length ? (isOpen ? 'Hide items' : 'Show items') : ''}
                    >
                      {lines.length > 0 && (isOpen ? <FiChevronDown size={12} /> : <FiChevronRight size={12} />)} {idx + 1}
                    </Td>
                    <Td>{formatDate(t.docDate)}</Td>
                    <Td>
                      <DocNumber
                        onClick={() => handleDocClick(t.tranTypeCode, t.id)}
                        title="Click to open document"
                      >
                        {t.docNumber || '-'} <FiExternalLink size={11} />
                      </DocNumber>
                    </Td>
                    <Td>{t.customerPO || t.reference || '-'}</Td>
                    <Td>
                      <TypeBadge type={t.tranTypeCode}>
                        {SALE_TYPE_LABELS[t.tranTypeCode] || t.tranTypeCode || '-'}
                      </TypeBadge>
                    </Td>
                    <Td>{t.partyName || '-'}</Td>
                    <Td style={{ whiteSpace: 'nowrap' }}>{ntnOf(t) || '-'}</Td>
                    <Td>{t.employeeName || '-'}</Td>
                    <AmountCell align="right">{formatCurrency(Number(t.totalAmount) || 0)}</AmountCell>
                    <AmountCell align="right">{formatCurrency(Number(t.totalDiscount) || 0)}</AmountCell>
                    <AmountCell align="right">{formatCurrency(Number(t.totalGST) || 0)}</AmountCell>
                    <AmountCell align="right">{formatCurrency(Number(t.totalWHT) || 0)}</AmountCell>
                    <AmountCell align="right" style={{ fontWeight: 600 }}>
                      {formatCurrency(Number(t.netAmount) || 0)}
                    </AmountCell>
                    <Td>
                      <StatusBadge $statusColor={getStatusColor(t.status)}>
                        {t.status || '-'}
                      </StatusBadge>
                    </Td>
                  </Tr>
                  {isOpen && (
                    <tr>
                      <Td colSpan={14} style={{ background: colors.neutral[50], padding: 0 }}>
                        <div style={{ padding: '8px 16px 12px 44px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                            <thead>
                              <tr style={{ color: colors.text.secondary }}>
                                <th style={DETAIL_TH}>Item</th>
                                <th style={{ ...DETAIL_TH, textAlign: 'right' }}>Qty</th>
                                <th style={DETAIL_TH}>Unit</th>
                                <th style={{ ...DETAIL_TH, textAlign: 'right' }}>Rate</th>
                                <th style={{ ...DETAIL_TH, textAlign: 'right' }}>Discount</th>
                                <th style={{ ...DETAIL_TH, textAlign: 'right' }}>Tax</th>
                                <th style={{ ...DETAIL_TH, textAlign: 'right' }}>Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {lines.length === 0 ? (
                                <tr><td colSpan={7} style={{ padding: '6px 8px', color: colors.text.tertiary }}>No line items</td></tr>
                              ) : lines.map((l, i) => (
                                <tr key={i}>
                                  <td style={DETAIL_TD}>{(l.itemCode ? l.itemCode + ' - ' : '') + (l.itemName || l.description || '-')}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatNumber(Number(l.quantity) || 0)}{l.unitCode || l.unitOfMeasure ? '' : ''}</td>
                                  <td style={DETAIL_TD}>{l.unitCode || l.unitOfMeasure || '-'}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatCurrency(Number(l.unitPrice) || 0)}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatCurrency(Number(l.discountAmount) || 0)}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatCurrency(Number(l.taxAmount) || 0)}</td>
                                  <td style={{ ...DETAIL_TD, textAlign: 'right' }}>{formatCurrency(Number(l.lineTotal) || 0)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </Td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}

                {/* Totals Row */}
                <TotalsRow>
                  <Td colSpan={8} style={{ textAlign: 'right', fontWeight: 700 }}>
                    Totals ({sortedTransactions.length} documents)
                  </Td>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>
                    {formatCurrency(summary.totalGrossAmount)}
                  </AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>
                    {formatCurrency(summary.totalDiscount)}
                  </AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>
                    {formatCurrency(summary.totalGST)}
                  </AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>
                    {formatCurrency(summary.totalWHT)}
                  </AmountCell>
                  <AmountCell align="right" style={{ fontWeight: 700 }}>
                    {formatCurrency(summary.totalNetAmount)}
                  </AmountCell>
                  <Td></Td>
                </TotalsRow>
              </Tbody>
            </Table>
          )}
        </TableContainer>
      </ContentCard>

      {/* Charts */}
      {hasLoaded && transactions.length > 0 && (
        <ChartsRow>
          <ChartCard>
            <ChartTitle>Document Count Trend</ChartTitle>
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={trendChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: colors.text.secondary }}
                  axisLine={{ stroke: colors.neutral[300] }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.text.secondary }}
                  axisLine={{ stroke: colors.neutral[300] }}
                  allowDecimals={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="count"
                  name="Documents"
                  stroke={CHART_COLORS[0]}
                  strokeWidth={2}
                  dot={{ r: 4, fill: CHART_COLORS[0] }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard>
            <ChartTitle>Amount by Document Type</ChartTitle>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={typeChartData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.neutral[200]} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: colors.text.secondary }}
                  axisLine={{ stroke: colors.neutral[300] }}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.text.secondary }}
                  axisLine={{ stroke: colors.neutral[300] }}
                  tickFormatter={(v) => formatCurrency(v, { compact: true, symbol: '' })}
                />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar
                  dataKey="amount"
                  name="Net Amount"
                  fill={CHART_COLORS[0]}
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </ChartsRow>
      )}
    </PageLayout>
  );
};

// Helper: format month label from YYYY-MM
const formatMonthLabel = (period) => {
  if (!period || period.length < 7) return period;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parts = period.split('-');
  const monthIdx = parseInt(parts[1], 10) - 1;
  return `${months[monthIdx] || parts[1]} ${parts[0].slice(2)}`;
};

// Helper: format short date from YYYY-MM-DD
const formatShortDate = (dateStr) => {
  if (!dateStr || dateStr.length < 10) return dateStr;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parts = dateStr.split('-');
  const monthIdx = parseInt(parts[1], 10) - 1;
  return `${parseInt(parts[2], 10)} ${months[monthIdx] || parts[1]}`;
};

// ============================================================
// Styled Components
// ============================================================

const FiltersCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  margin-bottom: 20px;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
`;

const DocTypeChecks = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  padding: 9px 10px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  background: white;
`;

const DocCheck = styled.label`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  color: ${colors.text.primary};
  cursor: pointer;
  white-space: nowrap;
  input { cursor: pointer; }
`;

const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  background: white;
  cursor: pointer;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${colors.neutral[300]};
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  color: ${colors.text.primary};
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: ${colors.primary.main};
    box-shadow: 0 0 0 3px ${colors.primary.main}15;
  }

  &::placeholder {
    color: ${colors.text.tertiary};
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: ${colors.borderRadius.md};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;

  ${props => props.primary ? `
    background: ${colors.primary.main};
    color: white;
    &:hover {
      background: ${colors.primary.dark};
    }
  ` : `
    background: ${colors.neutral[100]};
    color: ${colors.text.primary};
    &:hover {
      background: ${colors.neutral[200]};
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 20px;
`;

const SummaryCardStyled = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const SummaryIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: ${colors.borderRadius.md};
  background: ${props => props.color || colors.primary.main}15;
  color: ${props => props.color || colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
`;

const SummaryInfo = styled.div`
  min-width: 0;
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 22px;
  font-weight: 600;
  color: ${colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChartsRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const ChartCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  padding: 20px;
`;

const ChartTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin: 0 0 16px 0;
`;

const TooltipBox = styled.div`
  background: white;
  border: 1px solid ${colors.neutral[200]};
  border-radius: ${colors.borderRadius.md};
  padding: 12px;
  box-shadow: ${colors.shadows.md};
`;

const TooltipLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.primary};
  margin-bottom: 8px;
`;

const TooltipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${colors.text.secondary};
  margin-bottom: 4px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TooltipDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const ContentCard = styled.div`
  background: white;
  border-radius: ${colors.borderRadius.lg};
  box-shadow: ${colors.shadows.sm};
  overflow: hidden;
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${colors.neutral[50]};
`;

const Th = styled.th`
  padding: 12px 16px;
  text-align: ${props => props.align || 'left'};
  font-size: 12px;
  font-weight: 600;
  color: ${colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid ${colors.neutral[200]};
  white-space: nowrap;
`;

const ThSortable = styled(Th)`
  cursor: pointer;
  user-select: none;
  transition: color 0.2s;

  &:hover {
    color: ${colors.primary.main};
  }

  ${props => props.$active && `
    color: ${colors.primary.main};
  `}
`;

const SortIcon = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  vertical-align: middle;
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background 0.2s;

  &:hover {
    background: ${colors.neutral[50]};
  }
`;

const TotalsRow = styled.tr`
  background: ${colors.neutral[100]};
  border-top: 2px solid ${colors.neutral[300]};

  &:hover {
    background: ${colors.neutral[100]};
  }
`;

const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.text.primary};
  text-align: ${props => props.align || 'left'};
`;

const AmountCell = styled(Td)`
  font-family: 'SF Mono', 'Consolas', monospace;
  font-size: 13px;
`;

const DocNumber = styled.span`
  font-weight: 500;
  color: ${colors.primary.main};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: ${colors.primary.main}15;
    text-decoration: underline;
  }

  svg {
    opacity: 0.6;
  }

  &:hover svg {
    opacity: 1;
  }
`;

const TypeBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: ${colors.borderRadius.sm};
  font-size: 11px;
  font-weight: 500;
  background: ${props => {
    const t = props.type;
    if (t === 'SI' || t === 'PS') return '#FFF3E0';
    if (t === 'SR') return '#FFEBEE';
    if (t === 'DC') return '#E3F2FD';
    if (t === 'QT') return '#F3E5F5';
    if (t === 'SO') return '#E0F7FA';
    return colors.neutral[200];
  }};
  color: ${props => {
    const t = props.type;
    if (t === 'SI' || t === 'PS') return '#E65100';
    if (t === 'SR') return '#C62828';
    if (t === 'DC') return '#1565C0';
    if (t === 'QT') return '#7B1FA2';
    if (t === 'SO') return '#00838F';
    return colors.text.secondary;
  }};
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$statusColor?.bg || colors.neutral[100]};
  color: ${props => props.$statusColor?.color || colors.text.secondary};
  text-transform: capitalize;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: ${colors.text.secondary};
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  color: ${colors.neutral[300]};
  margin-bottom: 16px;
`;

const EmptyText = styled.p`
  font-size: 16px;
  margin-bottom: 8px;
`;

export default SaleRegister;
